import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider.js';
import { getAllClassrooms, getClassroomById, ALL_STUDENTS_CLASSROOM_ID } from '../services/classroomService.js';
import {
  assignStudentToClassroom,
  getAllStudents,
  getUnassignedStudents,
  removeStudentFromClassroom
} from '../services/studentService.js';
import essayService from '../services/essayService.js';
import '../styles/ClassroomDashboardPage.css';

const COMPETENCY_LABELS = {
  I: { label: 'Competência I', description: 'Domínio da norma-padrão da língua escrita.' },
  II: { label: 'Competência II', description: 'Compreensão da proposta e aplicação de repertório.' },
  III: { label: 'Competência III', description: 'Seleção, organização e interpretação de argumentos.' },
  IV: { label: 'Competência IV', description: 'Coesão e progressão textual.' },
  V: { label: 'Competência V', description: 'Proposta de intervenção detalhada e viável.' },
};

const formatScore = (score) => {
  if (typeof score !== 'number' || Number.isNaN(score)) {
    return '—';
  }
  return Math.round(score);
};

const formatDate = (timestamp) => {
  if (!timestamp) return '—';
  try {
    const date = typeof timestamp.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('pt-BR');
  } catch (err) {
    console.warn('Failed to format date', err);
    return '—';
  }
};

const getCompetencyMeta = (key, compData) => {
  const mapped = COMPETENCY_LABELS[key] || {};
  const label = compData?.label || mapped.label || `Competência ${key}`;
  const description = mapped.description || compData?.justification || '';
  const maxScore = compData?.maxScore || null;
  return { label, description, maxScore };
};

const StudentProgressChart = ({ scores }) => {
  if (!scores || scores.length === 0) {
    return <p className="chart-placeholder">Ainda não há redações corrigidas para este aluno.</p>;
  }

  const sorted = scores.slice().sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  const maxScore = Math.max(...sorted.map((item) => item.score), 100);
  const minScore = Math.min(...sorted.map((item) => item.score), 0);
  const range = Math.max(maxScore - minScore, 1);
  const pointSpacing = sorted.length > 1 ? 100 / (sorted.length - 1) : 100;

  const points = sorted
    .map((item, index) => {
      const x = sorted.length === 1 ? 50 : index * pointSpacing;
      const normalized = 100 - ((item.score - minScore) / range) * 90; // keep some padding at top/bottom
      const y = Math.min(Math.max(normalized, 5), 95);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg className="progress-chart" viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="Progresso do aluno">
      <polyline points={points} fill="none" stroke="var(--primary-color, #4f46e5)" strokeWidth="2" />
      {sorted.map((item, index) => {
        const x = sorted.length === 1 ? 50 : index * pointSpacing;
        const normalized = 100 - ((item.score - minScore) / range) * 90;
        const y = Math.min(Math.max(normalized, 5), 95);
        return <circle key={item.id} cx={x} cy={y} r={2.5} fill="var(--primary-color, #4f46e5)" />;
      })}
    </svg>
  );
};

function ClassroomDashboardPage() {
  const { classroomId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [classroom, setClassroom] = useState(null);
  const [students, setStudents] = useState([]);
  const [unassignedStudents, setUnassignedStudents] = useState([]);
  const [essays, setEssays] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [studentToAdd, setStudentToAdd] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [classroomLookup, setClassroomLookup] = useState({});
  const [rankCompetency, setRankCompetency] = useState('total');
  const [selectedCompetencyTrend, setSelectedCompetencyTrend] = useState(null);

  const isTeacherOrAdmin = currentUser && ['teacher', 'admin'].includes(currentUser.role);
  const isAdmin = currentUser?.role === 'admin';
  const isAggregateDashboard = classroomId === ALL_STUDENTS_CLASSROOM_ID;

  useEffect(() => {
    if (!currentUser) return;

    if (!isTeacherOrAdmin) {
      setError('Você não tem permissão para acessar este painel.');
      return;
    }

    if (isAggregateDashboard && !isAdmin) {
      setError('Apenas administradores podem acessar a visão consolidada de alunos.');
    } else {
      setError('');
    }
  }, [currentUser, isTeacherOrAdmin, isAggregateDashboard, isAdmin]);

  const fetchEssaysForClassroom = async (classStudentsList, classId) => {
    if (!Array.isArray(classStudentsList) || classStudentsList.length === 0) {
      return [];
    }

    const ids = classStudentsList.map((student) => student.id).filter(Boolean);
    if (ids.length === 0) {
      return [];
    }

    const essaysForStudents = await essayService.getEssaysForStudents(ids);

    const seenIds = new Set();
    const filtered = [];

    essaysForStudents.forEach((essay) => {
      if (!essay || !essay.studentId) {
        return;
      }

      if (seenIds.has(essay.id)) {
        return;
      }

      if (essay.classroomId && essay.classroomId !== classId) {
        return;
      }

      seenIds.add(essay.id);
      filtered.push(essay);
    });

    return filtered;
  };

  useEffect(() => {
    if (!classroomId || !isTeacherOrAdmin || !currentUser) return;

    let isMounted = true;

    const loadData = async () => {
      if (isAggregateDashboard && !isAdmin) {
        setClassroom(null);
        setStudents([]);
        setUnassignedStudents([]);
        setEssays([]);
        setClassroomLookup({});
        return;
      }

      setIsLoading(true);
      setFeedback('');

      try {
        if (isAggregateDashboard) {
          const [classroomData, classStudents, classrooms] = await Promise.all([
            getClassroomById(classroomId, { includeInvite: false }),
            getAllStudents(),
            getAllClassrooms()
          ]);

          const essaysForStudents = classStudents.length > 0
            ? await essayService.getEssaysForStudents(classStudents.map((student) => student.id))
            : [];

          if (!isMounted) return;

          const lookup = classrooms.reduce((acc, item) => {
            acc[item.id] = item.name;
            return acc;
          }, {});

          setClassroom(classroomData);
          setStudents(classStudents);
          setUnassignedStudents([]);
          setEssays(essaysForStudents);
          setClassroomLookup(lookup);
          setError('');
          return;
        }

        const [classroomData, classStudents, availablePool] = await Promise.all([
          getClassroomById(classroomId, { includeInvite: true }),
          getAllStudents(classroomId),
          getUnassignedStudents()
        ]);

        const classroomEssays = await fetchEssaysForClassroom(classStudents, classroomId);

        if (!isMounted) return;

        if (!classroomData) {
          setError('Sala não encontrada ou você não possui acesso.');
          setClassroom(null);
          setStudents([]);
          setUnassignedStudents([]);
          setEssays([]);
          setClassroomLookup({});
          return;
        }

        // Ensure the current teacher manages this classroom
        if (
          currentUser.role === 'teacher' &&
          Array.isArray(classroomData.teacherIds) &&
          !classroomData.teacherIds.includes(currentUser.uid)
        ) {
          setError('Você não pode acessar uma sala que não gerencia.');
          setClassroom(null);
          setStudents([]);
          setUnassignedStudents([]);
          setEssays([]);
          setClassroomLookup({});
          return;
        }

        setClassroom(classroomData);
        setStudents(classStudents);
        setUnassignedStudents(availablePool);
        setEssays(classroomEssays);
        setClassroomLookup({ [classroomData.id]: classroomData.name });
        setError('');
      } catch (err) {
        console.error('Failed to load classroom dashboard data', err);
        if (!isMounted) return;
        setError('Não foi possível carregar os dados da sala. Tente novamente mais tarde.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [classroomId, currentUser, isTeacherOrAdmin, isAggregateDashboard, isAdmin]);

  useEffect(() => {
    if (students.length > 0 && !selectedStudentId) {
      setSelectedStudentId(students[0].id);
    }
    if (students.length === 0 && selectedStudentId) {
      setSelectedStudentId(null);
    }
  }, [students, selectedStudentId]);

  const statsByStudent = useMemo(() => {
    const map = {};

    students.forEach((student) => {
      map[student.id] = {
        student,
        essays: [],
        gradedEssays: [],
        averageScore: null,
        bestScore: null,
        lastScore: null,
        competencyStats: {}
      };
    });

    essays.forEach((essay) => {
      const bucket = map[essay.studentId];
      if (!bucket) return;

      bucket.essays.push(essay);
      const totalScore = essay?.analysis?.totalScore;
      if (typeof totalScore === 'number' && Number.isFinite(totalScore)) {
        bucket.gradedEssays.push({
          id: essay.id,
          score: totalScore,
          createdAt: essay.createdAt || 0,
          title: essay.title || 'Redação'
        });
      }

      const compScores = essay?.analysis?.scores || {};
      Object.entries(compScores).forEach(([compKey, compData]) => {
        const val = Number(compData?.score);
        if (!Number.isFinite(val)) return;
        const meta = getCompetencyMeta(compKey, compData);
        if (!bucket.competencyStats[compKey]) {
          bucket.competencyStats[compKey] = {
            scores: [],
            trend: [],
            label: meta.label,
            description: meta.description,
            maxScore: meta.maxScore,
          };
        }
        bucket.competencyStats[compKey].scores.push(val);
        bucket.competencyStats[compKey].trend.push({
          score: val,
          createdAt: essay.createdAt || 0,
          id: `${essay.id || compKey}-${bucket.competencyStats[compKey].trend.length}`
        });
      });
    });

    Object.values(map).forEach((entry) => {
      if (entry.gradedEssays.length > 0) {
        const sum = entry.gradedEssays.reduce((acc, item) => acc + item.score, 0);
        entry.averageScore = sum / entry.gradedEssays.length;
        entry.bestScore = Math.max(...entry.gradedEssays.map((item) => item.score));
        entry.lastScore = entry.gradedEssays[entry.gradedEssays.length - 1].score;
      }
      entry.gradedEssays.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      entry.essays.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      Object.entries(entry.competencyStats).forEach(([key, comp]) => {
        if (!comp.scores.length) {
          entry.competencyStats[key] = { ...comp, average: null, latest: null };
          return;
        }
        const compTrend = comp.trend.slice().sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        const avg = comp.scores.reduce((acc, s) => acc + s, 0) / comp.scores.length;
        entry.competencyStats[key] = {
          ...comp,
          average: avg,
          latest: compTrend[compTrend.length - 1]?.score ?? null,
          trend: compTrend
        };
      });
    });

    return map;
  }, [essays, students]);

  const competencySummary = useMemo(() => {
    const map = {};
    essays.forEach((essay) => {
      const compScores = essay?.analysis?.scores || {};
      Object.entries(compScores).forEach(([key, compData]) => {
        const val = Number(compData?.score);
        if (!Number.isFinite(val)) return;
        const meta = getCompetencyMeta(key, compData);
        if (!map[key]) {
          map[key] = {
            label: meta.label,
            description: meta.description,
            maxScore: meta.maxScore,
            scores: [],
            trend: []
          };
        }
        map[key].scores.push(val);
        map[key].trend.push({
          score: val,
          createdAt: essay.createdAt || 0,
          id: `${essay.id || key}-${map[key].trend.length}`
        });
      });
    });

    return Object.entries(map).reduce((acc, [key, data]) => {
      const sortedTrend = data.trend.slice().sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      const avg = data.scores.length
        ? data.scores.reduce((sum, s) => sum + s, 0) / data.scores.length
        : null;
      acc[key] = {
        ...data,
        average: avg,
        latest: sortedTrend.length ? sortedTrend[sortedTrend.length - 1].score : null,
        trend: sortedTrend
      };
      return acc;
    }, {});
  }, [essays]);

  const rankedStudents = useMemo(() => {
    const key = rankCompetency;
    return Object.values(statsByStudent)
      .map((entry) => {
        const comp = key === 'total' ? null : entry.competencyStats[key];
        const sortScore = key === 'total' ? (entry.averageScore ?? 0) : (comp?.average ?? 0);
        return {
          ...entry,
          sortScore,
          gradedCount: key === 'total' ? entry.gradedEssays.length : (comp?.scores?.length || 0)
        };
      })
      .sort((a, b) => {
        if (b.sortScore === a.sortScore) {
          return b.gradedCount - a.gradedCount;
        }
        return b.sortScore - a.sortScore;
      });
  }, [statsByStudent, rankCompetency]);

  useEffect(() => {
    const compKeys = Object.keys(competencySummary);
    if (!selectedCompetencyTrend && compKeys.length > 0) {
      setSelectedCompetencyTrend(compKeys[0]);
    }
    if (rankCompetency !== 'total' && !competencySummary[rankCompetency]) {
      setRankCompetency('total');
    }
  }, [competencySummary, selectedCompetencyTrend, rankCompetency]);

  const selectedStats = selectedStudentId ? statsByStudent[selectedStudentId] : null;
  const selectedStudentClassroomName = selectedStats?.student?.classroomId
    ? classroomLookup[selectedStats.student.classroomId] || 'Sala desconhecida'
    : 'Sem turma';

  const availableStudents = isAggregateDashboard ? [] : unassignedStudents;
  const showClassroomColumn = isAggregateDashboard;
  const showActionsColumn = !isAggregateDashboard;
  const competencyKeys = Object.keys(competencySummary);
  const getCompetencyLabel = (key) => competencySummary[key]?.label || `Competência ${key}`;
  const getCompetencyDescription = (key, fallbackLabel) => {
    return COMPETENCY_LABELS[key]?.description || competencySummary[key]?.description || fallbackLabel || '';
  };

  const handleAssignStudent = async (event) => {
    event.preventDefault();
    if (isAggregateDashboard || !studentToAdd) return;

    setIsAssigning(true);
    setFeedback('');
    setError('');
    try {
      await assignStudentToClassroom(studentToAdd, classroomId);
      setFeedback('Aluno adicionado à sala com sucesso.');
      setStudentToAdd('');
      // reload data
      const [classStudents, availablePool] = await Promise.all([
        getAllStudents(classroomId),
        getUnassignedStudents()
      ]);
      const classroomEssays = await fetchEssaysForClassroom(classStudents, classroomId);
      setStudents(classStudents);
      setEssays(classroomEssays);
      setUnassignedStudents(availablePool);
    } catch (err) {
      console.error('Failed to assign student', err);
      setError(err.message || 'Não foi possível adicionar o aluno à sala.');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveStudent = async (studentId) => {
    if (isAggregateDashboard) return;
    if (!window.confirm('Remover este aluno da sala? Os dados da conta serão mantidos.')) {
      return;
    }

    setIsAssigning(true);
    setFeedback('');
    setError('');
    try {
      await removeStudentFromClassroom(studentId);
      setFeedback('Aluno removido da sala.');
      const [classStudents, availablePool] = await Promise.all([
        getAllStudents(classroomId),
        getUnassignedStudents()
      ]);
      const classroomEssays = await fetchEssaysForClassroom(classStudents, classroomId);
      setStudents(classStudents);
      setEssays(classroomEssays);
      setUnassignedStudents(availablePool);
      if (selectedStudentId === studentId) {
        setSelectedStudentId(null);
      }
    } catch (err) {
      console.error('Failed to remove student', err);
      setError(err.message || 'Não foi possível remover o aluno.');
    } finally {
      setIsAssigning(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="classroom-dashboard-container">
        <p>Faça login para acessar este recurso.</p>
      </div>
    );
  }

  if (!isTeacherOrAdmin) {
    return (
      <div className="classroom-dashboard-container">
        <p className="error-message">{error || 'Acesso negado.'}</p>
      </div>
    );
  }

  return (
    <div className="classroom-dashboard-container">
      <div className="dashboard-header">
        <div>
          <h2>{classroom?.name || 'Painel da sala'}</h2>
          {classroom?.description && <p className="classroom-description">{classroom.description}</p>}
        </div>
        <div className="header-actions">
          <button type="button" onClick={() => navigate('/manage/classrooms')} className="secondary-button">
            Voltar para salas
          </button>
          {classroom?.inviteUrl && (
            <a href={classroom.inviteUrl} target="_blank" rel="noopener noreferrer" className="secondary-button">
              Abrir link de convite
            </a>
          )}
        </div>
      </div>

      {isLoading ? (
        <p>Carregando dados da sala...</p>
      ) : (
        <>
          {(error || feedback) && (
            <div className={`feedback-banner ${error ? 'error' : 'success'}`}>
              {error || feedback}
            </div>
          )}

          <section className="summary-cards">
            <div className="summary-card">
              <span className="summary-title">Alunos</span>
              <strong className="summary-value">{students.length}</strong>
            </div>
            <div className="summary-card">
              <span className="summary-title">Redações enviadas</span>
              <strong className="summary-value">{essays.length}</strong>
            </div>
            <div className="summary-card">
              <span className="summary-title">Média da turma</span>
              <strong className="summary-value">
                {formatScore(
                  (() => {
                    const graded = essays.filter((essay) => {
                      const totalScore = essay?.analysis?.totalScore;
                      return typeof totalScore === 'number' && Number.isFinite(totalScore);
                    });
                    if (graded.length === 0) return null;
                    const sum = graded.reduce((acc, essay) => acc + essay.analysis.totalScore, 0);
                    return sum / graded.length;
                  })()
                )}
              </strong>
            </div>
          </section>

          {competencyKeys.length > 0 && (
            <section className="panel">
              <div className="panel-header">
                <div>
                  <h3>Competências da turma</h3>
                  <p className="panel-subtitle">Notas e evolução por competência</p>
                </div>
                <label className="inline-label">
                  <span>Ver evolução</span>
                  <select
                    value={selectedCompetencyTrend || ''}
                    onChange={(e) => setSelectedCompetencyTrend(e.target.value || null)}
                  >
                    {competencyKeys.map((key) => (
                      <option key={key} value={key}>
                        {getCompetencyLabel(key)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="competency-grid">
                {competencyKeys.map((key) => {
                  const comp = competencySummary[key];
                  return (
                    <div key={key} className="competency-card">
                      <div className="competency-card-header">
                        <h4>{comp.label}</h4>
                        {comp.maxScore && <span className="muted">/ {comp.maxScore}</span>}
                      </div>
                      {comp.description && <p className="competency-description">{comp.description}</p>}
                      <p className="competency-metric">
                        Média: <strong>{formatScore(comp.average)}</strong>
                      </p>
                      <p className="competency-metric muted">
                        Última: {formatScore(comp.latest)}
                      </p>
                    </div>
                  );
                })}
              </div>

              {selectedCompetencyTrend && competencySummary[selectedCompetencyTrend]?.trend?.length > 0 && (
                <div className="competency-trend">
                  <h4>Evolução em {getCompetencyLabel(selectedCompetencyTrend)}</h4>
                  <StudentProgressChart scores={competencySummary[selectedCompetencyTrend].trend} />
                </div>
              )}
            </section>
          )}

          <section className="manage-students-panel">
            <h3>{isAggregateDashboard ? 'Alunos cadastrados' : 'Gerenciar alunos'}</h3>
            {isAggregateDashboard ? (
              <p className="panel-subtitle">
                Visão consolidada de todos os alunos — utilize os dashboards individuais das salas para gerenciar matrículas.
              </p>
            ) : (
              <form className="assign-form" onSubmit={handleAssignStudent}>
                <label htmlFor="studentToAdd">Adicionar aluno existente</label>
                <div className="assign-controls">
                  <select
                    id="studentToAdd"
                    value={studentToAdd}
                    onChange={(event) => setStudentToAdd(event.target.value)}
                    disabled={isAssigning || availableStudents.length === 0}
                  >
                    <option value="" disabled>
                      {availableStudents.length === 0 ? 'Nenhum aluno disponível' : 'Selecione um aluno'}
                    </option>
                    {availableStudents.map((student) => (
                      <option key={student.id} value={student.id}>
                        {student.name} ({student.email})
                      </option>
                    ))}
                  </select>
                  <button type="submit" disabled={isAssigning || !studentToAdd}>
                    {isAssigning ? 'Salvando...' : 'Adicionar'}
                  </button>
                </div>
              </form>
            )}

            <div className="student-table-wrapper">
              <table className="students-table">
                <thead>
                  <tr>
                    <th>Aluno</th>
                    {showClassroomColumn && <th>Sala</th>}
                    <th>Redações corrigidas</th>
                    <th>Média</th>
                    <th>Melhor nota</th>
                    <th>Última atualização</th>
                    {showActionsColumn && <th>Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {students.length === 0 ? (
                    <tr>
                      <td colSpan={showClassroomColumn ? 6 : 5}>
                        {isAggregateDashboard
                          ? 'Nenhum aluno cadastrado até o momento.'
                          : 'Nenhum aluno matriculado nesta sala ainda.'}
                      </td>
                    </tr>
                  ) : (
                    students.map((student) => {
                      const entry = statsByStudent[student.id];
                      const latestEssay = entry?.essays?.[0];
                      const classroomName = student.classroomId
                        ? classroomLookup[student.classroomId] || 'Sala desconhecida'
                        : 'Sem turma';
                      return (
                        <tr key={student.id} className={selectedStudentId === student.id ? 'selected-row' : ''}>
                          <td>
                            <button
                              type="button"
                              className="link-like"
                              onClick={() => setSelectedStudentId(student.id)}
                            >
                              {student.name}
                            </button>
                          </td>
                          {showClassroomColumn && <td>{classroomName}</td>}
                          <td>{entry?.gradedEssays.length || 0}</td>
                          <td>{formatScore(entry?.averageScore)}</td>
                          <td>{formatScore(entry?.bestScore)}</td>
                          <td>{formatDate(latestEssay?.createdAt)}</td>
                          {showActionsColumn && (
                            <td>
                              <button
                                type="button"
                                className="danger-link"
                                onClick={() => handleRemoveStudent(student.id)}
                                disabled={isAssigning}
                              >
                                Remover
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="dashboard-grid">
            <div className="panel">
              <h3>Evolução do aluno</h3>
              {selectedStats ? (
                <>
                  <div className="panel-header">
                    <div>
                      <h4>{selectedStats.student.name}</h4>
                      <p className="panel-subtitle">
                        Média {formatScore(selectedStats.averageScore)} · Melhor {formatScore(selectedStats.bestScore)}
                        {isAggregateDashboard && (
                          <>
                            {' '}
                            · {selectedStudentClassroomName}
                          </>
                        )}
                      </p>
                    </div>
                    <Link
                      to={`/manage/students?highlight=${selectedStats.student.id}`}
                      className="secondary-button"
                    >
                      Abrir ficha do aluno
                    </Link>
                  </div>
                  <StudentProgressChart scores={selectedStats.gradedEssays} />
                  {Object.keys(selectedStats.competencyStats || {}).length > 0 && (
                    <div className="competency-grid compact">
                      {Object.entries(selectedStats.competencyStats).map(([key, comp]) => (
                        <div key={key} className="competency-card">
                          <div className="competency-card-header">
                            <h4>{comp.label}</h4>
                            {comp.maxScore && <span className="muted">/ {comp.maxScore}</span>}
                          </div>
                          {comp.description && <p className="competency-description">{comp.description}</p>}
                          <p className="competency-metric">Média: <strong>{formatScore(comp.average)}</strong></p>
                          <p className="competency-metric muted">Última: {formatScore(comp.latest)}</p>
                          {comp.trend?.length > 0 && (
                            <StudentProgressChart scores={comp.trend} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p>Selecione um aluno para visualizar o desempenho.</p>
              )}
            </div>

            <div className="panel">
              <div className="panel-header">
                <h3>Ranking da sala</h3>
                <label className="inline-label">
                  <span>Ordenar por</span>
                  <select value={rankCompetency} onChange={(e) => setRankCompetency(e.target.value)}>
                    <option value="total">Média geral</option>
                    {competencyKeys.map((key) => (
                      <option key={key} value={key}>
                        {getCompetencyLabel(key)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <ol className="ranking-list">
                {rankedStudents.length === 0 && <li>Nenhuma redação corrigida ainda.</li>}
                {rankedStudents.map((entry, index) => (
                  <li key={entry.student.id}>
                    <span className="rank-position">#{index + 1}</span>
                    <div className="rank-details">
                      <strong>{entry.student.name}</strong>
                      {rankCompetency === 'total' ? (
                        <span>{formatScore(entry.averageScore)} pontos · {entry.gradedEssays.length} redações</span>
                      ) : (
                        <span>
                          {getCompetencyLabel(rankCompetency)} · {formatScore(entry.competencyStats[rankCompetency]?.average)} pontos
                        </span>
                      )}
                      {isAggregateDashboard && (
                        <small className="rank-classroom">
                          {entry.student.classroomId
                            ? classroomLookup[entry.student.classroomId] || 'Sala desconhecida'
                            : 'Sem turma'}
                        </small>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          <section className="panel">
            <h3>Redações do aluno selecionado</h3>
            {selectedStats && selectedStats.essays.length > 0 ? (
              <div className="essay-table-wrapper">
                <table className="essays-table">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Data</th>
                      <th>Nota</th>
                      <th>Correção</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStats.essays.map((essay) => {
                      const totalScore = essay?.analysis?.totalScore;
                      return (
                        <tr key={essay.id}>
                          <td>{essay.title || 'Redação'}</td>
                          <td>{formatDate(essay.createdAt)}</td>
                          <td>{formatScore(totalScore)}</td>
                          <td>
                            <Link to={`/enem-training/essay/${essay.id}`} className="secondary-button">
                              Ver detalhes
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Nenhuma redação registrada para este aluno ainda.</p>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default ClassroomDashboardPage;
